<template>
  <modal-wrapper :shown="favoritesModalShown" @hide="hideFavoritesModal">
    <div class="grid h-75screen" style="grid-template-rows: auto 1fr auto">
      <div>
        <div class="px-4 text-lg font-bold">Favorites</div>
        <div
          class="grid px-4 mt-6"
          style="grid-template-columns: auto 1fr auto"
        >
          <button
            class="flex items-center justify-center w-12 h-16 bg-white border-r-2 border-gray-300 dark:border-gray-700 dark:bg-gray-600 rounded-tl-md rounded-bl-md focus:outline-none"
            @click="
              groupToEditIndex = selectedFavoritesGroupIndex;
              showEditGroupModal();
            "
          >
            <pencil-icon class="w-6 h-6" />
          </button>
          <label
            for="favoritesSelect"
            class="relative h-16 overflow-hidden bg-white dark:bg-gray-600"
          >
            <select
              id="favoritesSelect"
              v-model="selectedFavoritesGroupIndex"
              class="w-full h-full pl-3 pr-10 font-semibold bg-transparent appearance-none focus:outline-none"
            >
              <option
                v-for="(favorite, index) in favorites"
                :key="index"
                :value="index"
              >
                {{ favorite.name }}
              </option>
            </select>
            <div class="absolute top-0 right-0 flex items-center h-full pr-2">
              <vertical-chevrons-icon class="w-6 h-6" />
            </div>
          </label>
          <button
            class="flex items-center justify-center w-12 h-16 bg-white border-l-2 border-gray-300 dark:border-gray-700 dark:bg-gray-600 rounded-tr-md rounded-br-md focus:outline-none"
            @click="showCreateGroupModal"
          >
            <plus-icon class="w-6 h-6" />
          </button>
        </div>
      </div>

      <div
        class="flex items-center justify-center px-4 mt-8"
        v-show="favorites[selectedFavoritesGroupIndex].rolls.length === 0"
      >
        <div class="font-medium text-center text-gray-600 dark:text-gray-300">
          This group contains no favorites.
        </div>
      </div>
      <transition-group
        class="flex flex-col px-4 mt-6 space-y-3 overflow-x-hidden overflow-y-auto"
        tag="div"
        name="block-list"
      >
        <div
          class="flex text-sm text-left text-white rounded-md"
          :class="{
            'bg-purple-600': favorite.color === 'purple',
            'bg-blue-600': favorite.color === 'blue',
            'bg-cyan-600': favorite.color === 'cyan',
            'bg-green-600': favorite.color === 'green',
            'bg-yellow-600': favorite.color === 'yellow',
            'bg-orange-600': favorite.color === 'orange',
            'bg-red-600': favorite.color === 'red',
          }"
          v-for="(favorite, index) in favorites[selectedFavoritesGroupIndex]
            .rolls"
          :key="favorite.creationMs"
        >
          <div
            class="flex flex-col w-12 border-r-2"
            :class="{
              'border-purple-700': favorite.color === 'purple',
              'border-blue-700': favorite.color === 'blue',
              'border-cyan-700': favorite.color === 'cyan',
              'border-green-700': favorite.color === 'green',
              'border-yellow-700': favorite.color === 'yellow',
              'border-orange-700': favorite.color === 'orange',
              'border-red-700': favorite.color === 'red',
            }"
          >
            <button
              class="flex items-center justify-center flex-1 border-b focus:outline-none"
              :class="{
                'border-purple-700': favorite.color === 'purple',
                'border-blue-700': favorite.color === 'blue',
                'border-cyan-700': favorite.color === 'cyan',
                'border-green-700': favorite.color === 'green',
                'border-yellow-700': favorite.color === 'yellow',
                'border-orange-700': favorite.color === 'orange',
                'border-red-700': favorite.color === 'red',
              }"
              @click="moveFavoriteUp(index)"
            >
              <chevron-up-icon class="w-5 h-5" />
            </button>
            <button
              class="flex items-center justify-center flex-1 border-t focus:outline-none"
              :class="{
                'border-purple-700': favorite.color === 'purple',
                'border-blue-700': favorite.color === 'blue',
                'border-cyan-700': favorite.color === 'cyan',
                'border-green-700': favorite.color === 'green',
                'border-yellow-700': favorite.color === 'yellow',
                'border-orange-700': favorite.color === 'orange',
                'border-red-700': favorite.color === 'red',
              }"
              @click="moveFavoriteDown(index)"
            >
              <chevron-down-icon class="w-5 h-5" />
            </button>
          </div>
          <button
            class="flex flex-col justify-center flex-1 h-16 pl-4 text-left focus:outline-none"
            @click="
              rollFavorite(favorites[selectedFavoritesGroupIndex], favorite)
            "
          >
            <div class="font-medium text-white opacity-75">
              {{ favorite.name }}
            </div>
            <div class="mt-1 font-semibold text-white">
              {{ favorite.notation }}
            </div>
          </button>
          <button
            @click="
              favoriteToEditIndex = index;
              showEditFavoriteModal();
            "
            class="flex items-center justify-center w-12 h-16 border-l-2 focus:outline-none"
            :class="{
              'border-purple-700': favorite.color === 'purple',
              'border-blue-700': favorite.color === 'blue',
              'border-cyan-700': favorite.color === 'cyan',
              'border-green-700': favorite.color === 'green',
              'border-yellow-700': favorite.color === 'yellow',
              'border-orange-700': favorite.color === 'orange',
              'border-red-700': favorite.color === 'red',
            }"
          >
            <pencil-icon class="w-6 h-6" />
          </button>
        </div>
      </transition-group>
      <div class="px-4 mt-6">
        <action-button @click="showCreateFavoriteModal">
          Add new favorite
        </action-button>
        <action-button class="mt-4" type="outline" @click="hideFavoritesModal">
          Close
        </action-button>
      </div>
    </div>
    <portal to="modals">
      <create-favorite-modal />
      <edit-favorite-modal :favoriteToEdit="favoriteToEdit" />
      <create-group-modal />
      <edit-group-modal :groupToEdit="groupToEdit" />
    </portal>
  </modal-wrapper>
</template>

<script>
import { call, get, sync } from "vuex-pathify";

import ChevronUpIcon from "@/components/icons/ChevronUpIcon";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon";
import PencilIcon from "@/components/icons/PencilIcon";
import PlusIcon from "@/components/icons/PlusIcon";
import VerticalChevronsIcon from "@/components/icons/VerticalChevronsIcon";

import ModalWrapper from "@/components/ModalWrapper";
import CreateFavoriteModal from "@/components/modals/CreateFavoriteModal";
import EditFavoriteModal from "@/components/modals/EditFavoriteModal";
import CreateGroupModal from "@/components/modals/CreateGroupModal";
import EditGroupModal from "@/components/modals/EditGroupModal";

import ActionButton from "@/components/inputs/ActionButton";

export default {
  name: "FavoritesModal",
  components: {
    ChevronUpIcon,
    ChevronDownIcon,
    PencilIcon,
    PlusIcon,
    VerticalChevronsIcon,
    ModalWrapper,
    CreateFavoriteModal,
    EditFavoriteModal,
    CreateGroupModal,
    EditGroupModal,
    ActionButton,
  },
  data() {
    return {
      favoriteToEditIndex: null,
      groupToEditIndex: null,
      createFavoriteModalShown: false,
      editFavoriteModalShown: false,
      createGroupModalShown: false,
      editGroupModalShown: false,
    };
  },
  computed: {
    favorites: get("favorites"),
    favoritesModalShown: sync("favoritesModalShown"),
    selectedFavoritesGroupIndex: sync("selectedFavoritesGroupIndex"),
    favoriteToEdit() {
      return {
        favoriteIndex: this.favoriteToEditIndex,
        groupIndex: this.selectedFavoritesGroupIndex,
        data: this.favorites[this.selectedFavoritesGroupIndex].rolls[
          this.favoriteToEditIndex
        ],
      };
    },
    groupToEdit() {
      return {
        groupIndex: this.groupToEditIndex,
        data: this.favorites[this.groupToEditIndex],
      };
    },
    showFavoritesGroupNameInLog: get("showFavoritesGroupNameInLog"),
  },
  methods: {
    roll: call("roll"),
    hideFavoritesModal: call("hideFavoritesModal"),
    rollFavorite(group, favorite) {
      this.hideFavoritesModal();
      this.roll({
        notation: favorite.notation,
        name: this.showFavoritesGroupNameInLog
          ? `${group.name} - ${favorite.name}`
          : favorite.name,
      });
    },
    moveFavoriteUp(favoriteIndex) {
      const selectedFavorites = this.favorites[this.selectedFavoritesGroupIndex]
        .rolls;
      const favorite = selectedFavorites.splice(favoriteIndex, 1)[0];
      selectedFavorites.splice(favoriteIndex - 1, 0, favorite);
    },
    moveFavoriteDown(favoriteIndex) {
      const selectedFavorites = this.favorites[this.selectedFavoritesGroupIndex]
        .rolls;
      const favorite = selectedFavorites.splice(favoriteIndex, 1)[0];
      selectedFavorites.splice(favoriteIndex + 1, 0, favorite);
    },
    hideCreateFavoriteModal: call("hideCreateFavoriteModal"),
    showCreateFavoriteModal: call("showCreateFavoriteModal"),
    hideEditFavoriteModal: call("hideEditFavoriteModal"),
    showEditFavoriteModal: call("showEditFavoriteModal"),
    hideCreateGroupModal: call("hideCreateGroupModal"),
    showCreateGroupModal: call("showCreateGroupModal"),
    hideEditGroupModal: call("hideEditGroupModal"),
    showEditGroupModal: call("showEditGroupModal"),
  },
};
</script>

<style>
.block-list-move {
  transition: transform 0.15s;
}
.block-list-enter-active,
.block-list-leave-active {
  transition: all 0.15s;
}
.block-list-enter,
.block-list-leave-to {
  opacity: 0;
  transform: translateX(4rem);
}
</style>